import React, { useEffect, useState } from 'react';
import { avatarColorClass, avatarInitials } from '../lib/avatar';

/**
 * A person's picture: their photo when they have one, otherwise their initials
 * on a colour derived from their name (`lib/avatar.ts`).
 *
 * A photo that fails to load (stale Google URL, expired S3 presign, missing
 * object) falls back to the initials too, so no list ever shows a
 * broken-image icon. Nothing leaves the browser to draw it — see `lib/avatar.ts`
 * for why that matters.
 *
 * `name` is what the initials and the colour come from: pass the FULL name
 * (first + last) when there is one, so "Pau Torrent" reads "PT" and not "P".
 */
export interface AvatarProps {
  /** Photo URL. Empty/null → initials. */
  src?: string | null;
  /** Who it is. Drives the initials and the colour. */
  name?: string | null;
  /** Side in px. Default 32. */
  size?: number;
  /** `circle` (lists, comments) or `rounded` (the sidebar trigger). */
  shape?: 'circle' | 'rounded';
  /** Greyed out, for inactive users. */
  inactive?: boolean;
  /** Extra classes (borders, rings, shadows). */
  className?: string;
  /** Tooltip. */
  title?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 32,
  shape = 'circle',
  inactive = false,
  className = '',
  title,
}) => {
  const [failed, setFailed] = useState(false);
  // A new URL deserves a fresh try (e.g. the user just uploaded a photo).
  useEffect(() => setFailed(false), [src]);

  const base = [
    'shrink-0',
    shape === 'circle' ? 'rounded-full' : 'rounded-lg',
    inactive ? 'grayscale opacity-50' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const box = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        // Google avatars are refused when a Referer is sent.
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        alt=""
        title={title}
        style={box}
        className={`${base} object-cover`}
      />
    );
  }

  // Two letters don't fit a 16-20 px dot; one does.
  const initials = avatarInitials(name);
  const text = size < 22 ? Array.from(initials)[0] : initials;

  return (
    <span
      aria-hidden="true"
      title={title}
      style={{ ...box, fontSize: Math.max(8, Math.round(size * 0.4)) }}
      className={`${base} ${avatarColorClass(name)} inline-flex select-none items-center justify-center font-bold leading-none`}
    >
      {text}
    </span>
  );
};

export default Avatar;
